/**
 * Operações e gestores do cliente — o cliente de rede de
 * `/api/customers/:id/operations` e `/api/customers/:id/managers` (Story 24.4/24.5;
 * a «área/projeto» passou a operação na Story 38.1).
 *
 * Espelho fino de `apps/api/src/routes/customers.ts`, no molde de
 * `lib/api/allocations.ts`: cookie de sessão sempre incluído, non-2xx a lançar
 * `ApiError` com a frase do servidor (`detail`), tipos do contrato vindos de
 * `@caliber/shared/dtos` (só tipos: nada do zod entra no chunk da tela).
 *
 * MÓDULO PRÓPRIO, e não mais funções em `lib/api/customers.ts`: estas leituras
 * são feitas por diálogos que vivem fora da ficha do cliente (o convite de
 * `/settings/users`), e um módulo à parte é o que se mocka à parte nos testes.
 *
 * INATIVAR, NUNCA APAGAR: não há `DELETE` de operação nem de gestor. Uma operação
 * inativa sai das escolhas NOVAS (alocar, mudar de operação, dar acesso); quem já
 * está nela continua lá.
 */
import type {
  BillRateUnit,
  CreateCustomerOperation,
  Currency,
  CustomerManager,
  CustomerOperation,
  CustomerOperationOption,
  CustomerRecordStatus,
  OperationDeliveryManagerPeriod,
  UpdateCustomerOperation,
} from '@caliber/shared/dtos'

import { API_BASE } from '@/lib/api'
import { parseProblemJson } from '@/lib/api/problem'
import { ApiError } from '@/lib/api/users'

export type {
  BillRateUnit,
  Currency,
  CustomerManager,
  CustomerOperation,
  CustomerOperationOption,
  CustomerRecordStatus,
  OperationDeliveryManagerPeriod,
}
export { ApiError }

const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' }

/**
 * A chave das operações completas de um cliente (TODAS, ativas e inativas) — só a aba
 * das operações, de quem gere a conta (Story 38.2).
 */
export function customerOperationsKey(customerId: string) {
  return ['customer-operations', customerId] as const
}

/**
 * A chave da lista básica (Story 38.2), que os selects e a aba de quem não gere a conta
 * leem. Fica sob a `customerOperationsKey`: invalidar essa refaz também esta.
 */
export function customerOperationOptionsKey(customerId: string) {
  return ['customer-operations', customerId, 'options'] as const
}

/** A chave do histórico do Delivery Manager de uma operação. */
export function operationDeliveryManagersKey(operationId: string) {
  return ['operation-delivery-managers', operationId] as const
}

/** A chave dos gestores de um cliente (todos). */
export function customerManagersKey(customerId: string) {
  return ['customer-managers', customerId] as const
}

/**
 * Um erro com o CAMPO a que pertence — o 409 do nome repetido (`field: 'name'`), o
 * 422 da conta do gestor (`field: 'userId'`), e os 422 do Delivery Manager
 * (`deliveryManagerUserId`) e dos gestores da operação (`managerIds`). A tela põe a
 * frase por baixo do campo, e não num toast: é ali que ela se corrige.
 *
 * O `field` vem na RAIZ do corpo (o `problemJson` do servidor espalha os
 * `extensions` lá).
 */
export class FieldProblemError extends ApiError {
  readonly field: string

  constructor(status: number, message: string, field: string) {
    super(status, message)
    this.name = 'FieldProblemError'
    this.field = field
  }
}

/**
 * O erro de uma resposta recusada (Story 38.6): a frase do servidor (`detail`) e, quando a
 * recusa diz o campo (`field`), um `FieldProblemError` — o molde de `allocations.ts`, para
 * os clientes que o partilham (a vaga, o evento da candidatura).
 */
export async function problemFromResponse(res: Response, fallback: string): Promise<ApiError> {
  const body = await parseProblemJson(res)
  const detail = typeof body?.detail === 'string' && body.detail !== '' ? body.detail : null
  const message = detail ?? fallback
  const field = typeof body?.field === 'string' && body.field !== '' ? body.field : null
  return field !== null
    ? new FieldProblemError(res.status, message, field)
    : new ApiError(res.status, message)
}

function fallback(status: number): string {
  if (status === 401) return 'Sua sessão expirou. Entre novamente para continuar.'
  if (status === 403)
    return 'Só um administrador ou o account manager deste cliente abrem e editam as operações.'
  if (status === 404) return 'Este cliente, operação ou gestor não existe mais.'
  return `Não foi possível completar a operação (${status})`
}

async function request<T>(url: string, init: RequestInit): Promise<T> {
  const res = await fetch(url, { credentials: 'include', ...init })
  if (res.ok) return (await res.json()) as T

  // A frase pt-BR do servidor vive em `detail`; o `title` é a constante inglesa,
  // e só serve quando não há `detail`.
  const body = await parseProblemJson(res)
  const message = textOf(body?.detail) ?? textOf(body?.title) ?? fallback(res.status)
  const field = textOf(body?.field)
  if (field !== null) throw new FieldProblemError(res.status, message, field)
  throw new ApiError(res.status, message)
}

function textOf(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null
}

function base(customerId: string): string {
  return `${API_BASE}/customers/${encodeURIComponent(customerId)}`
}

function getInit(signal?: AbortSignal): RequestInit {
  const init: RequestInit = { headers: { Accept: 'application/json' } }
  if (signal) init.signal = signal
  return init
}

// ------------------------------------------------------------
// Operações
// ------------------------------------------------------------

/**
 * `GET /api/customers/:id/operation-options` — a lista básica (Story 38.2): todas (ativas
 * e inativas), por nome, só com os campos de identificação. Qualquer usuário Genesis lê:
 * é o que os selects usam.
 */
export async function listCustomerOperationOptions(
  customerId: string,
  signal?: AbortSignal,
): Promise<CustomerOperationOption[]> {
  const body = await request<{ items: CustomerOperationOption[] }>(
    `${base(customerId)}/operation-options`,
    getInit(signal),
  )
  return body.items
}

/**
 * `GET /api/customers/:id/operations` — todas (ativas e inativas), por nome, com o
 * Delivery Manager atual e os gestores vinculados. Só quem gere a conta (403 aos
 * outros, Story 38.2).
 */
export async function listCustomerOperations(
  customerId: string,
  signal?: AbortSignal,
): Promise<CustomerOperation[]> {
  const body = await request<{ items: CustomerOperation[] }>(
    `${base(customerId)}/operations`,
    getInit(signal),
  )
  return body.items
}

/**
 * `POST /api/customers/:id/operations` — nasce ativa. 409 (`field: 'name'`) = nome
 * repetido; 422 = Delivery Manager ou gestores recusados.
 */
export async function createCustomerOperation(
  customerId: string,
  input: CreateCustomerOperation,
): Promise<CustomerOperation> {
  return request<CustomerOperation>(`${base(customerId)}/operations`, {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify(input),
  })
}

/**
 * `PATCH /api/customers/:id/operations/:operationId` — só os campos que mudam: um
 * campo ausente fica como está.
 */
export async function updateCustomerOperation(
  customerId: string,
  operationId: string,
  input: UpdateCustomerOperation,
): Promise<CustomerOperation> {
  return request<CustomerOperation>(
    `${base(customerId)}/operations/${encodeURIComponent(operationId)}`,
    { method: 'PATCH', headers: jsonHeaders, body: JSON.stringify(input) },
  )
}

/** `GET …/operations/:operationId/delivery-managers` — o histórico, do mais recente ao mais antigo. */
export async function listOperationDeliveryManagers(
  customerId: string,
  operationId: string,
  signal?: AbortSignal,
): Promise<OperationDeliveryManagerPeriod[]> {
  const body = await request<{ items: OperationDeliveryManagerPeriod[] }>(
    `${base(customerId)}/operations/${encodeURIComponent(operationId)}/delivery-managers`,
    getInit(signal),
  )
  return body.items
}

// ------------------------------------------------------------
// Gestores
// ------------------------------------------------------------

/** O corpo de criar/editar um gestor. `email: null` = sem e-mail; `userId: null` = sem conta. */
export interface CustomerManagerInput {
  name?: string
  email?: string | null
  /** O cargo no cliente (Story 34.2); `null` = sem cargo. */
  title?: string | null
  /**
   * A conta do portal, SE o gestor tiver uma: o acesso ao portal desde a Story 38.7 (a
   * conta vê as posições e os profissionais das operações dele). Um usuário DESTE cliente
   * ou uma pessoa da Genesis com o perfil dele (422 senão), e não a de outro gestor ativo
   * (409). Dar, trocar ou tirar é só do admin (403).
   */
  userId?: string | null
  /** Mudar o estado de um gestor COM conta é só do admin (403). */
  status?: CustomerRecordStatus
}

/** `GET /api/customers/:id/managers` — todos, por nome. */
export async function listCustomerManagers(
  customerId: string,
  signal?: AbortSignal,
): Promise<CustomerManager[]> {
  const body = await request<{ items: CustomerManager[] }>(
    `${base(customerId)}/managers`,
    getInit(signal),
  )
  return body.items
}

/** `POST /api/customers/:id/managers` — nasce ativo. 422 (`field: 'userId'`) = conta de outro cliente. */
export async function createCustomerManager(
  customerId: string,
  input: CustomerManagerInput & { name: string },
): Promise<CustomerManager> {
  return request<CustomerManager>(`${base(customerId)}/managers`, {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify(input),
  })
}

/**
 * `PATCH /api/customers/:id/managers/:managerId` — editar e/ou inativar/reativar. 409 =
 * a conta já é de outro gestor ativo (`field: 'userId'`), ou o gestor mudou entretanto.
 */
export async function updateCustomerManager(
  customerId: string,
  managerId: string,
  input: CustomerManagerInput,
): Promise<CustomerManager> {
  return request<CustomerManager>(`${base(customerId)}/managers/${encodeURIComponent(managerId)}`, {
    method: 'PATCH',
    headers: jsonHeaders,
    body: JSON.stringify(input),
  })
}

/**
 * `PUT /api/customers/:id/managers/:managerId/operations` (Story 38.7) — as operações do
 * gestor, a lista inteira: é o que a conta dele vê no portal. Só quem gere a conta (403).
 * 422 (`field: 'operationIds'`) = uma operação de outro cliente, uma inativa nova, ou
 * uma nova para um gestor inativo; 409 = sai uma operação em que ele é o responsável de
 * posições vivas (a frase do servidor diz quais).
 */
export async function updateManagerOperations(
  customerId: string,
  managerId: string,
  operationIds: string[],
): Promise<{ operationIds: string[] }> {
  return request<{ operationIds: string[] }>(
    `${base(customerId)}/managers/${encodeURIComponent(managerId)}/operations`,
    { method: 'PUT', headers: jsonHeaders, body: JSON.stringify({ operationIds }) },
  )
}
